import React from "react";
import { Modal, Button, Input, Radio, Row, Col, Switch, Image } from "antd";
import {
    SettingOutlined,
    CheckCircleFilled,
    CameraOutlined,
    BankOutlined,
    UserOutlined,
    CalendarOutlined,
    PictureOutlined,
    NumberOutlined,
    FileTextOutlined,
    LayoutOutlined
} from "@ant-design/icons";

const SettingsModal = ({
    visible,
    onCancel,
    onSave,
    saving,
    report,
    editTitle,
    setEditTitle,
    editLayout,
    setEditLayout,
    claimNumber,
    setClaimNumber,
    showConclusion,
    setShowConclusion,
    conclusion,
    setConclusion,
    coverSettings,
    setCoverSettings,
    layoutOptions
}) => {
    const handleCancel = () => {
        setEditTitle(report.title);
        setEditLayout(report.photos_per_page || report.photosPerPage || 2);
        onCancel();
    };

    const handleAfterOpenChange = (open) => {
        if (open) {
            setEditTitle(report.title);
            setEditLayout(report.photos_per_page || report.photosPerPage || 2);
            setClaimNumber(report.claim_number || "");
            setShowConclusion(report.show_conclusion ?? true);
            setConclusion(report.conclusion || '');
            if (report.cover_settings) {
                setCoverSettings({
                    showCoverPhoto: report.cover_settings.show_cover_photo ?? true,
                    showCompanyLogo: report.cover_settings.show_company_logo ?? true,
                    showCompanyName: report.cover_settings.show_company_name ?? true,
                    showCreatedDate: report.cover_settings.show_created_date ?? true,
                    showPhotoCount: report.cover_settings.show_photo_count ?? true,
                    showClaimNumber: report.cover_settings.show_claim_number ?? true,
                    coverPhotoId: report.cover_settings.cover_photo_id || null
                });
            }
        }
    };

    return (
        <Modal
            className="photo-report-setting-model"
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '18px', fontWeight: '600' }}>
                    <SettingOutlined style={{ color: '#1890ff' }} />
                    Report Settings
                </div>
            }
            open={visible}
            width={720}
            onCancel={handleCancel}
            afterOpenChange={handleAfterOpenChange}
            footer={[
                <Button key="cancel" onClick={handleCancel} size="large">
                    Cancel
                </Button>,
                <Button 
                    key="save" 
                    type="primary" 
                    onClick={async () => {
                        const success = await onSave();
                        if (success) {
                            onCancel();
                        }
                    }} 
                    loading={saving} 
                    size="large" 
                    icon={<CheckCircleFilled />}
                >
                    Save Settings
                </Button>
            ]}
        >
            <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '8px 4px' }}>
                {/* Report Title and Claim Number Section */}
                <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #e8e8e8', marginBottom: '20px' }}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <FileTextOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                                <label style={{ fontWeight: '600', fontSize: '15px', margin: 0 }}>Report Title</label>
                            </div>
                            <Input
                                className="formControl"
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                size="large"
                                placeholder="Enter report title"
                            />
                        </Col>
                        <Col xs={24} md={12}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <NumberOutlined style={{ fontSize: '16px', color: '#fa541c' }} />
                                <label style={{ fontWeight: '600', fontSize: '15px', margin: 0 }}>Claim Number</label>
                            </div>
                            <Input
                                className="formControl"
                                value={claimNumber}
                                onChange={(e) => setClaimNumber(e.target.value)}
                                size="large"
                                placeholder="#123456"
                            />
                        </Col>
                    </Row>
                </div>

                {/* Cover Page Settings Section */}
                <div style={{ background: '#fafafa', padding: '20px', borderRadius: '8px', border: '1px solid #e8e8e8', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                        <CameraOutlined style={{ fontSize: '18px', color: '#52c41a' }} />
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Cover Page Settings</h3>
                    </div>

                    <Row gutter={[12, 12]}>
                        <Col xs={24} sm={12}>
                            <div style={{ background: '#fff', padding: '14px 16px', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e8e8e8', transition: 'all 0.3s' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <PictureOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
                                    <span style={{ fontWeight: '500' }}>Cover Photo</span>
                                </div>
                                <Switch
                                    checked={coverSettings.showCoverPhoto}
                                    onChange={(checked) => setCoverSettings(prev => ({ ...prev, showCoverPhoto: checked }))}
                                />
                            </div>
                        </Col>
                        <Col xs={24} sm={12}>
                            <div style={{ background: '#fff', padding: '14px 16px', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e8e8e8', transition: 'all 0.3s' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <BankOutlined style={{ fontSize: '16px', color: '#fa8c16' }} />
                                    <span style={{ fontWeight: '500' }}>Company Logo</span>
                                </div>
                                <Switch
                                    checked={coverSettings.showCompanyLogo}
                                    onChange={(checked) => setCoverSettings(prev => ({ ...prev, showCompanyLogo: checked }))}
                                />
                            </div>
                        </Col>
                        <Col xs={24} sm={12}>
                            <div style={{ background: '#fff', padding: '14px 16px', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e8e8e8', transition: 'all 0.3s' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <UserOutlined style={{ fontSize: '16px', color: '#722ed1' }} />
                                    <span style={{ fontWeight: '500' }}>Company Name</span>
                                </div>
                                <Switch
                                    checked={coverSettings.showCompanyName}
                                    onChange={(checked) => setCoverSettings(prev => ({ ...prev, showCompanyName: checked }))}
                                />
                            </div>
                        </Col>
                        <Col xs={24} sm={12}>
                            <div style={{ background: '#fff', padding: '14px 16px', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e8e8e8', transition: 'all 0.3s' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <CalendarOutlined style={{ fontSize: '16px', color: '#13c2c2' }} />
                                    <span style={{ fontWeight: '500' }}>Created Date</span>
                                </div>
                                <Switch
                                    checked={coverSettings.showCreatedDate}
                                    onChange={(checked) => setCoverSettings(prev => ({ ...prev, showCreatedDate: checked }))}
                                />
                            </div>
                        </Col>
                        <Col xs={24} sm={12}>
                            <div style={{ background: '#fff', padding: '14px 16px', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e8e8e8', transition: 'all 0.3s' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <CameraOutlined style={{ fontSize: '16px', color: '#eb2f96' }} />
                                    <span style={{ fontWeight: '500' }}>Photo Count</span>
                                </div>
                                <Switch
                                    checked={coverSettings.showPhotoCount}
                                    onChange={(checked) => setCoverSettings(prev => ({ ...prev, showPhotoCount: checked }))}
                                />
                            </div>
                        </Col>
                        <Col xs={24} sm={12}>
                            <div style={{ background: '#fff', padding: '14px 16px', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e8e8e8', transition: 'all 0.3s' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <NumberOutlined style={{ fontSize: '16px', color: '#fa541c' }} />
                                    <span style={{ fontWeight: '500' }}>Claim Number</span>
                                </div>
                                <Switch
                                    checked={coverSettings.showClaimNumber}
                                    onChange={(checked) => setCoverSettings(prev => ({ ...prev, showClaimNumber: checked }))}
                                />
                            </div>
                        </Col>
                        <Col xs={24} sm={12}>
                            <div style={{ background: '#fff', padding: '14px 16px', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e8e8e8', transition: 'all 0.3s' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <FileTextOutlined style={{ fontSize: '16px', color: '#52c41a' }} />
                                    <span style={{ fontWeight: '500' }}>Conclusion</span>
                                </div>
                                <Switch
                                    checked={showConclusion}
                                    onChange={(checked) => setShowConclusion(checked)}
                                />
                            </div>
                        </Col>
                    </Row>

                    {coverSettings.coverPhotoId && (
                        <div style={{ marginTop: '16px', padding: '12px 16px', background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)', borderRadius: '6px', border: '1px solid #91d5ff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <CheckCircleFilled style={{ fontSize: '16px', color: '#1890ff' }} />
                            <span style={{ fontWeight: '500', color: '#0050b3' }}>Cover photo selected from section images</span>
                        </div>
                    )}
                </div>

                {/* Photo Page Layout Section */}
                <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #e8e8e8' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                        <LayoutOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Photo Page Layout</h3>
                    </div>
                    <p style={{ color: '#8c8c8c', marginBottom: '16px', fontSize: '13px' }}>Select how many photos to display per page</p>

                    <Radio.Group
                        className="layout-radio-group"
                        value={editLayout}
                        onChange={(e) => setEditLayout(e.target.value)}
                        style={{ width: '100%' }}
                    >
                        {layoutOptions.length > 0 ? (
                            layoutOptions.map((option) => (
                                <Radio key={option} value={option} className="layout-card">
                                    <div className="layout-content">
                                        <div>
                                            <div className="layout-title">{option} Photos per Page</div>
                                        </div>
                                        <div className="layout-icon">
                                            <Image preview={false} src={`https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2025/12/11020031/layout-image-${option}693a6c0fa7ab9693a6c0fa7b4d.png`} />
                                        </div>
                                    </div>
                                </Radio>
                            ))
                        ) : (
                            // Fallback to default options if API fails
                            <>
                                <Radio value={2} className="layout-card">
                                    <div className="layout-content">
                                        <div>
                                            <div className="layout-title">2 Photos per Page</div>
                                        </div>
                                        <div className="layout-icon">
                                            <Image preview={false} src="https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2025/12/11020031/layout-image-2693a6c0fa7ab9693a6c0fa7b4d.png" />
                                        </div>
                                    </div>
                                </Radio>
                                <Radio value={3} className="layout-card">
                                    <div className="layout-content">
                                        <div>
                                            <div className="layout-title">3 Photos per Page</div>
                                        </div>
                                        <div className="layout-icon">
                                            <Image preview={false} src="https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2025/12/11020024/layout-image-3693a6c08d58cd693a6c08d5974.png" />
                                        </div>
                                    </div>
                                </Radio>
                                <Radio value={4} className="layout-card">
                                    <div className="layout-content">
                                        <div>
                                            <div className="layout-title">4 Photos per Page</div>
                                        </div>
                                        <div className="layout-icon">
                                            <Image preview={false} src="https://fddatainsp.s3.us-east-2.amazonaws.com/wp-content/uploads/2025/12/11020017/layout-image-4693a6c01df2f7693a6c01df389.png" />
                                        </div>
                                    </div>
                                </Radio>
                            </>
                        )}
                    </Radio.Group>
                </div>
            </div>
        </Modal>
    );
};

export default SettingsModal;


